import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";

/* ── Helpers ──────────────────────────────────────────────────────────────── */
const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES  = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function startOfMonth(year, month) {
  return new Date(year, month, 1);
}
function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}
/** Parse a date string like "2026-09-21" without timezone shift */
function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toYMD(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/* ── Status badge colours ─────────────────────────────────────────────────── */
const STATUS_STYLE = {
  approved: "bg-success text-success",
  pending:  "bg-warning text-warning",
  complete: "bg-primary/10 text-primary",
  rejected: "bg-destructive/10 text-destructive",
};

/* ── Component ────────────────────────────────────────────────────────────── */
export default function CalendarPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();

  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  /* fetch once */
  useEffect(() => {
    authAxios().get("/events")
      .then((r) => setEvents(r.data))
      .catch(() => toast.error("Failed to load events"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Build a map: "YYYY-MM-DD" → event[] (any event that spans that day) */
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const start = parseLocalDate(ev.start_date);
      const end   = parseLocalDate(ev.end_date) || start;
      if (!start) continue;
      const cur = new Date(start);
      while (cur <= end) {
        const key = toYMD(cur);
        (map[key] = map[key] || []).push(ev);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [events]);

  /* Calendar grid */
  const { gridDays, firstWeekday } = useMemo(() => {
    const firstWeekday = startOfMonth(year, month).getDay();
    const total = daysInMonth(year, month);
    const gridDays = [];
    for (let i = 0; i < firstWeekday; i++) gridDays.push(null);
    for (let d = 1; d <= total; d++) gridDays.push(d);
    /* pad to complete last week */
    while (gridDays.length % 7 !== 0) gridDays.push(null);
    return { gridDays, firstWeekday };
  }, [year, month]);

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const todayYMD = toYMD(today);

  return (
    <div className="p-6 md:p-8 lg:p-12 w-full space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-heading text-3xl font-bold text-foreground">Calendar</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToday}>Today</Button>
          <Button variant="ghost" size="icon" onClick={prevMonth} aria-label="Previous month">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <span className="font-heading font-semibold text-lg min-w-[11rem] text-center">
            {MONTH_NAMES[month]} {year}
          </span>
          <Button variant="ghost" size="icon" onClick={nextMonth} aria-label="Next month">
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
        {/* Day-of-week header */}
        <div className="grid grid-cols-7 border-b border-border">
          {DAYS_OF_WEEK.map((d) => (
            <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground tracking-wide">
              {d}
            </div>
          ))}
        </div>

        {/* Weeks */}
        {loading ? (
          <div className="h-96 flex items-center justify-center text-muted-foreground text-sm">
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {gridDays.map((day, idx) => {
              const ymd = day ? `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : null;
              const dayEvents = ymd ? (eventsByDay[ymd] || []) : [];
              const isToday   = ymd === todayYMD;
              const isWeekend = idx % 7 === 0 || idx % 7 === 6;
              const isLastRow = idx >= gridDays.length - 7;

              return (
                <div
                  key={idx}
                  className={[
                    "min-h-[100px] p-1.5 border-b border-r border-border",
                    !day ? "bg-muted/30" : isWeekend ? "bg-muted/10" : "bg-card",
                    isLastRow ? "border-b-0" : "",
                    idx % 7 === 6 ? "border-r-0" : "",
                  ].join(" ")}
                >
                  {day && (
                    <>
                      {/* Day number */}
                      <div className="flex justify-end mb-1">
                        <span
                          className={[
                            "flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium",
                            isToday
                              ? "bg-primary text-primary-foreground font-bold"
                              : "text-muted-foreground",
                          ].join(" ")}
                        >
                          {day}
                        </span>
                      </div>

                      {/* Event chips — show max 3, +N overflow */}
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map((ev) => (
                          <button
                            key={ev.event_id}
                            onClick={() => navigate(`/events/${ev.event_id}`)}
                            title={ev.title}
                            className={[
                              "w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] font-medium leading-tight transition-opacity hover:opacity-80",
                              STATUS_STYLE[ev.status] || "bg-muted text-muted-foreground",
                            ].join(" ")}
                          >
                            {ev.title}
                          </button>
                        ))}
                        {dayEvents.length > 3 && (
                          <p className="text-[10px] text-muted-foreground pl-1">
                            +{dayEvents.length - 3} more
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="font-medium">Status:</span>
        {Object.entries(STATUS_STYLE).map(([status, cls]) => (
          <span key={status} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${cls}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        ))}
      </div>

      {/* This-month event list */}
      <div className="space-y-2">
        <h2 className="font-heading font-semibold text-foreground">
          Events in {MONTH_NAMES[month]}
        </h2>
        {(() => {
          const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
          const monthEvents = events.filter(
            (ev) => (ev.start_date || "").startsWith(monthStr) ||
                    (ev.end_date   || "").startsWith(monthStr)
          );
          if (!monthEvents.length) {
            return <p className="text-sm text-muted-foreground">No events this month.</p>;
          }
          return monthEvents.map((ev) => (
            <button
              key={ev.event_id}
              onClick={() => navigate(`/events/${ev.event_id}`)}
              className="w-full text-left flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-card transition-colors hover:border-primary hover:bg-secondary"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-foreground truncate">{ev.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ""}
                  {ev.location ? ` · ${ev.location}` : ""}
                </p>
              </div>
              <Badge
                className={`shrink-0 text-[10px] font-semibold ${STATUS_STYLE[ev.status] || "bg-muted text-muted-foreground"}`}
              >
                {ev.status}
              </Badge>
            </button>
          ));
        })()}
      </div>
    </div>
  );
}
